/**
 * Shared with `ApprovalCard.tsx`, whose comment on `renderTarget` explains why
 * a host and username sourced from scanned asset data need the same defence
 * as model-generated text: both are attacker-reachable, so anywhere either
 * renders here gets the same treatment.
 */

/**
 * Explicit Unicode bidirectional formatting characters: the embedding and
 * override pair (U+202A-U+202E), the isolates (U+2066-U+2069), and the two
 * implicit marks (U+200E/U+200F).
 *
 * Written as escapes on purpose. Spelling these out literally would put
 * invisible reordering characters into this very file, so a reviewer reading
 * the defence would have their own editor rearrange the line describing it.
 */
const BIDI_CONTROLS = /[\u202A-\u202E\u2066-\u2069\u200E\u200F]/g

/**
 * Defuse a Trojan Source attack (CVE-2021-42574, applied to a UI rather than a
 * compiler) by making bidi controls VISIBLE instead of EFFECTIVE.
 *
 * `echo safe\u202E dangerous` renders as `echo safe suoregnad` — the reviewer
 * reproduced it — so a user can approve a command whose displayed order is not
 * its execution order. Argument values are model-generated, which is exactly
 * the channel a prompt injection controls, and this card's whole premise is
 * that a hostile value is SEEN. Reordering defeats that as completely as
 * truncation would.
 *
 * Escaped rather than stripped: deleting the character would silently rewrite
 * what the user is being asked to authorise, and a legitimate one (Persian
 * text in a `write_file` body genuinely uses these) would vanish with no
 * indication. `\u202E` on screen is both honest and inert.
 *
 * No `dir` covers this, which is why the escaping has to exist at all. A
 * container direction — `ChatInner` used to pin `dir='ltr'`, and now inherits
 * the locale's — only settles PARAGRAPH-level spillover. An embedded override
 * reorders within the run whatever the paragraph around it is doing, so it
 * survives any value the surrounding markup could pick.
 */
export function neutraliseBidi(text: string): string {
  return text.replace(
    BIDI_CONTROLS,
    (char) => `\\u${char.codePointAt(0)!.toString(16).toUpperCase().padStart(4, '0')}`,
  )
}
